import {useMemo, useState, type ReactNode} from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import {Link as RouterLink} from 'react-router'
import {AUTH_SIGN_UP} from './splash-chrome'
import demoData from './series-demo-fixtures.json'

export interface SeriesDemoFixture {
  id: string
  title: string
  make: 'Story' | 'Audio course'
  genre: 'Cyberpunk' | 'Fantasy' | null
  topicBundle: string
  brief: string
  topics: string[]
  paragraphs: string[]
  highlights: {text: string; topic: string; annotation: string}[]
}

type Make = 'Story' | 'Audio course'
type Genre = 'Cyberpunk' | 'Fantasy'
type TopicBundle = 'AI + business' | 'Cooking' | 'No learning topics'

export const SERIES_DEMO_FIXTURES = demoData.fixtures as SeriesDemoFixture[]
const fixtureById = Object.fromEntries(SERIES_DEMO_FIXTURES.map((fixture) => [fixture.id, fixture]))

function fixtureId(make: Make, genre: Genre, topics: TopicBundle) {
  if (make === 'Audio course') return topics === 'Cooking' ? 'course-cooking' : 'course-agents-business'
  const world = genre.toLowerCase()
  if (topics === 'No learning topics') return `${world}-fiction`
  return `${world}-${topics === 'Cooking' ? 'cooking' : 'agents-business'}`
}

function HighlightedParagraph({text, fixture}: {text: string; fixture: SeriesDemoFixture}) {
  const highlights = fixture.highlights.filter((highlight) => text.includes(highlight.text))
  if (!highlights.length) return <Typography component="p" sx={{fontFamily: 'Georgia, serif', fontSize: {xs: '1rem', md: '1.08rem'}, lineHeight: 1.72, m: 0}}>{text}</Typography>

  const ordered = [...highlights].sort((a, b) => text.indexOf(a.text) - text.indexOf(b.text))
  const parts: ReactNode[] = []
  let cursor = 0
  ordered.forEach((highlight) => {
    const start = text.indexOf(highlight.text, cursor)
    if (start < 0) return
    if (start > cursor) parts.push(text.slice(cursor, start))
    parts.push(<Tooltip key={`${highlight.topic}-${start}`} title={`${highlight.topic}: ${highlight.annotation}`} arrow describeChild enterTouchDelay={0} leaveTouchDelay={6000}>
      <Box component="mark" tabIndex={0} sx={{bgcolor: 'action.selected', color: 'inherit', borderBottom: '2px solid', borderColor: 'splash.accent', px: 0.25, cursor: 'help', '&:focus-visible': {outline: '2px solid', outlineColor: 'splash.accent', outlineOffset: '2px'}}}>{highlight.text}</Box>
    </Tooltip>)
    cursor = start + highlight.text.length
  })
  if (cursor < text.length) parts.push(text.slice(cursor))
  return <Typography component="p" sx={{fontFamily: 'Georgia, serif', fontSize: {xs: '1rem', md: '1.08rem'}, lineHeight: 1.72, m: 0}}>{parts}</Typography>
}

function Choice({label, value, selected, onChange}: {label: string; value: string; selected: string; onChange: (value: string) => void}) {
  return <Stack spacing={0.35}>
    <Typography component="span" variant="caption" sx={{fontWeight: 700}}>{label}</Typography>
    <ToggleButtonGroup exclusive fullWidth size="small" value={selected} onChange={(_event, next) => next && onChange(next)} aria-label={label}>
      {value.split('|').map((option) => <ToggleButton key={option} value={option} sx={{textTransform: 'none', fontWeight: 600, px: {xs: 0.5, sm: 1}, py: {xs: 0.5, sm: 0.75}, fontSize: {xs: '0.72rem', sm: '0.8125rem'}}}>{option}</ToggleButton>)}
    </ToggleButtonGroup>
  </Stack>
}

export default function SeriesDemo({compact = false}: {compact?: boolean}) {
  const [make, setMake] = useState<Make>('Story')
  const [genre, setGenre] = useState<Genre>('Cyberpunk')
  const [topics, setTopics] = useState<TopicBundle>('AI + business')
  const id = fixtureId(make, genre, topics)
  const fixture = fixtureById[id]!
  const visibleParagraphs = useMemo(() => fixture.paragraphs.slice(0, compact ? 3 : 5), [fixture, compact])

  const changeMake = (next: Make) => {
    setMake(next)
    if (next === 'Audio course' && topics === 'No learning topics') setTopics('AI + business')
  }

  return <Box data-testid="series-demo" sx={{borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', color: 'text.primary', boxShadow: 4}}>
    <Stack spacing={compact ? 1.4 : 2} sx={{p: {xs: 2, sm: 3, md: compact ? 3 : 4}, minHeight: {md: compact ? 330 : 390}, bgcolor: 'background.default'}}>
      <Box>
        <Typography variant="overline" component="p" sx={{color: 'splash.accent', fontWeight: 800, lineHeight: 1.2}}>Illustrative snippet</Typography>
        <Typography variant="caption" color="text.secondary">{fixture.make}{fixture.genre ? ` · ${fixture.genre}` : ''} · {fixture.topicBundle}</Typography>
        <Typography variant="h4" component="h2" sx={{fontFamily: 'Georgia, serif', fontWeight: 700, mt: 0.5}}>{fixture.title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{mt: 0.75}}>{fixture.brief}</Typography>
      </Box>
      {!!fixture.topics.length && <Stack direction="row" spacing={0.75} useFlexGap sx={{flexWrap: 'wrap'}}>{fixture.topics.map((topic) => <Chip key={topic} label={topic} size="small" variant="outlined" />)}</Stack>}
      <Stack spacing={1.35} sx={{flex: 1}}>{visibleParagraphs.map((paragraph) => <HighlightedParagraph key={paragraph} text={paragraph} fixture={fixture} />)}</Stack>
    </Stack>

    <Stack spacing={1.5} sx={{p: {xs: 1.5, sm: 2.5}, bgcolor: 'action.hover', borderTop: '1px solid', borderColor: 'divider'}}>
      <Typography variant="body2" color="text.secondary">Choose a combination to see a different example.</Typography>
      <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: make === 'Story' ? 'repeat(3, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))'}, gap: 1.25}}>
        <Choice label="Make" value="Story|Audio course" selected={make} onChange={(value) => changeMake(value as Make)} />
        {make === 'Story' && <Choice label="Genre" value="Cyberpunk|Fantasy" selected={genre} onChange={(value) => setGenre(value as Genre)} />}
        <Choice label="Topics" value={make === 'Story' ? 'AI + business|Cooking|No learning topics' : 'AI + business|Cooking'} selected={topics} onChange={(value) => setTopics(value as TopicBundle)} />
      </Box>
      <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
        <Button component={RouterLink} to={AUTH_SIGN_UP} variant="contained">Get started free</Button>
      </Box>
    </Stack>
  </Box>
}
